import { useEffect } from 'react';
import { UseFormReturn, useFormState, useWatch } from 'react-hook-form';
import { TextInputField } from '@brighte/form-components';
import { CodeAcceptanceStatus, ContactDetailsType, VerificationType } from '@brighte/validation';
import { datadogRum } from '@datadog/browser-rum';
import { Field } from '@spark-web/field';
import { TextInput } from '@spark-web/text-input';
import { AuthenticationFlowType } from '../graphql';
import { usePasswordlessUserVerification } from '../hooks';
import { LABELS } from '../utils';
import { VerificationDetailsType } from './ContactFields';
import SecureCode, { SecureFields } from './SecureCode';

type UpdateFieldsProps = {
  onResetCode?: (data: unknown) => void;
  isMobileOnly: boolean;
  isEmailOnly: boolean;
  isMobileAndEmail: boolean;
  verificationDetails?: VerificationDetailsType;
} & UseFormReturn<ContactDetailsType & SecureFields>;

type ValidTextInputType = 'email' | 'tel';

export function ValidationFields({
  onResetCode,
  isMobileOnly,
  isEmailOnly,
  isMobileAndEmail,
  verificationDetails,
  ...formContext
}: UpdateFieldsProps) {
  const { EMAIL, MOBILE_NUMBER } = LABELS;
  const { register, setFocus, control } = formContext;
  const { errors } = useFormState({ control });
  const [codeAccepted, otpSent] = useWatch({ control, name: ['codeAccepted', 'otpSent'] });

  const { createPasswordlessUser, createPasswordlessUserLoading } = usePasswordlessUserVerification();

  const maskedMobile = verificationDetails?.mobile
    ? verificationDetails?.mobile.substring(0, 6).replace(/[0-9]/g, '*') +
      verificationDetails?.mobile.substring(6, verificationDetails?.mobile.length)
    : '';

  // TODO: fix type, email and mobile must be defined
  const otpData = {
    email: verificationDetails?.email,
    mobile: verificationDetails?.mobile,
    type:
      verificationDetails?.verificationType === VerificationType.Email
        ? AuthenticationFlowType.EMAIL
        : AuthenticationFlowType.SMS,
  };

  useEffect(() => {
    if (otpSent) {
      setFocus?.('otp');
    }
  }, [otpSent, setFocus]);

  useEffect(() => {
    const emailErrorMessage = errors.email?.message as string | undefined;
    const mobileErrorMessage = errors.mobile?.message as string | undefined;

    if (isMobileOnly && emailErrorMessage) {
      datadogRum.addAction('Contact verification hidden field error', {
        field: 'email',
        message: emailErrorMessage,
      });
    }

    if (isEmailOnly && mobileErrorMessage) {
      datadogRum.addAction('Contact verification hidden field error', {
        field: 'mobile',
        message: mobileErrorMessage,
      });
    }
  }, [errors.email?.message, errors.mobile?.message, isEmailOnly, isMobileOnly]);

  const fields: {
    name: keyof Pick<ContactDetailsType, 'email' | 'mobile'>;
    label: string;
    isHidden: boolean;
    type: ValidTextInputType;
  }[] = [
    { name: 'email', label: EMAIL, isHidden: isMobileOnly, type: 'email' },
    { name: 'mobile', label: MOBILE_NUMBER, isHidden: isEmailOnly, type: 'tel' },
  ];

  const handleResetCode = data => {
    createPasswordlessUser({
      email: data.email,
      mobile: data.mobile,
      verificationType: data.type === AuthenticationFlowType.EMAIL ? VerificationType.Email : VerificationType.Sms,
    });
    onResetCode?.(data);
  };

  const renderInputField = (
    name: keyof Pick<ContactDetailsType, 'email' | 'mobile'>,
    label: string,
    isHidden: boolean,
    type: ValidTextInputType,
  ) => {
    const fieldErrorMessage = errors[name]?.message as string | undefined;
    if (isHidden) {
      return <input key={name} type="hidden" {...register(name)} />;
    }

    if (name === 'mobile' && !!maskedMobile) {
      return (
        <Field
          label={label}
          key={name}
          readOnly
          tone={fieldErrorMessage ? 'critical' : undefined}
          message={fieldErrorMessage}
        >
          <TextInput value={maskedMobile} overflowStrategy="nowrap" />
        </Field>
      );
    }

    return (
      <TextInputField
        key={name}
        control={control}
        name={name}
        label={label}
        FieldProps={{ readOnly: true }}
        type={type}
        overflowStrategy="nowrap"
      />
    );
  };

  return (
    <>
      {fields.map(({ name, label, isHidden, type }) => renderInputField(name, label, isHidden, type))}

      {otpSent ? (
        <SecureCode
          isMobile={isMobileAndEmail || isMobileOnly}
          isCodeAccepted={codeAccepted && codeAccepted !== CodeAcceptanceStatus.NotAccepted}
          data={otpData}
          loading={createPasswordlessUserLoading}
          onResetCode={handleResetCode}
          {...register('otp')}
          name="otp"
          {...formContext}
        />
      ) : null}
    </>
  );
}
